/**
 * Utility functions for working with MUI X v8 GridRowSelectionModel
 *
 * In v8, GridRowSelectionModel changed from GridRowId[] to:
 * { type: 'include' | 'exclude'; ids: Set<GridRowId> }
 *
 * These utilities help migrate code that expects array operations.
 */

import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';

/**
 * Create an empty selection model
 */
export function createEmptySelectionModel(): GridRowSelectionModel {
  return {
    type: 'include',
    ids: new Set<GridRowId>(),
  };
}

/**
 * Create a selection model from an array of IDs
 */
export function createSelectionModel(ids: GridRowId[]): GridRowSelectionModel {
  return {
    type: 'include',
    ids: new Set(ids),
  };
}

/**
 * Convert selection model to array (only works for 'include' type)
 * For 'exclude' type, you need access to all row IDs to compute the result
 */
export function selectionToArray(model: GridRowSelectionModel): GridRowId[] {
  if (model.type === 'exclude') {
    throw new Error(
      'Cannot convert exclude selection model to array without all row IDs. Use gridRowSelectionManagerSelector instead.',
    );
  }
  return Array.from(model.ids);
}

/**
 * Check if a row ID is selected
 */
export function isRowSelected(
  model: GridRowSelectionModel,
  rowId: GridRowId,
): boolean {
  if (model.type === 'include') {
    return model.ids.has(rowId);
  } else {
    // exclude type: selected if NOT in the exclusion set
    return !model.ids.has(rowId);
  }
}

/**
 * Get the count of selected items (for 'include' type only)
 * For 'exclude' type, you need to know total row count
 */
export function getSelectionCount(model: GridRowSelectionModel): number {
  if (model.type === 'exclude') {
    throw new Error(
      'Cannot get selection count for exclude model without total row count. Use gridRowSelectionManagerSelector instead.',
    );
  }
  return model.ids.size;
}

/**
 * Check if selection model is empty
 */
export function isSelectionEmpty(model: GridRowSelectionModel): boolean {
  return model.ids.size === 0;
}

/**
 * Add a row ID to the selection model (returns new model)
 */
export function addToSelection(
  model: GridRowSelectionModel,
  rowId: GridRowId,
): GridRowSelectionModel {
  const newIds = new Set(model.ids);
  if (model.type === 'include') {
    newIds.add(rowId);
  } else {
    newIds.delete(rowId); // exclude: remove from exclusion set
  }
  return { ...model, ids: newIds };
}

/**
 * Remove a row ID from the selection model (returns new model)
 */
export function removeFromSelection(
  model: GridRowSelectionModel,
  rowId: GridRowId,
): GridRowSelectionModel {
  const newIds = new Set(model.ids);
  if (model.type === 'include') {
    newIds.delete(rowId);
  } else {
    newIds.add(rowId); // exclude: add to exclusion set
  }
  return { ...model, ids: newIds };
}
